import { useEffect, useState } from 'react'
import { APP_STORE_URL, PLAY_STORE_URL } from '../lib/app-links'

// groundops.app/get → the field app, whatever device you're holding.
//
// The stable public download URL for the invite email's QR + link, the
// dormant SMS invite, and any printed material. Phones are redirected
// straight to their store (no tap needed); desktops and platforms we can't
// serve yet see this page — a QR to hand the link to a phone, plus the store
// button. Same client-side-redirect convention as /signup; store targets
// live in ONE place (src/lib/app-links.ts).
//
// iPadOS 13+ masquerades as macOS ("MacIntel" + touch), so the desktop view
// deliberately shows the App Store button too — an iPad tap still works.

type Platform = 'ios' | 'android' | 'other'

function detectPlatform(): Platform {
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'other'
}

export default function GetApp() {
  const [platform, setPlatform] = useState<Platform>('other')

  useEffect(() => {
    document.title = 'Get the GroundOps Field app'
    const detected = detectPlatform()
    setPlatform(detected)
    if (detected === 'ios') window.location.replace(APP_STORE_URL)
    if (detected === 'android' && PLAY_STORE_URL) window.location.replace(PLAY_STORE_URL)
  }, [])

  const androidPending = platform === 'android' && !PLAY_STORE_URL

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.25rem',
        padding: '3rem 1.5rem',
        textAlign: 'center',
        fontFamily: "-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        color: '#181D27',
        backgroundColor: '#FFFFFF',
      }}
    >
      <h1 style={{ margin: 0, fontSize: '1.75rem', lineHeight: 1.25 }}>Get the GroundOps Field app</h1>

      {platform === 'ios' ? (
        <p style={{ margin: 0, color: '#414651' }}>
          Taking you to the App Store… if nothing happens,{' '}
          <a href={APP_STORE_URL} style={{ color: '#181D27' }}>tap here</a>.
        </p>
      ) : androidPending ? (
        <p style={{ margin: 0, maxWidth: '26rem', color: '#414651' }}>
          The Android app is coming soon. In the meantime, office users can work in the web app at{' '}
          <a href="https://app.groundops.app/login" style={{ color: '#181D27' }}>app.groundops.app</a>.
        </p>
      ) : (
        <>
          <p style={{ margin: 0, maxWidth: '26rem', color: '#414651' }}>
            Scan the code with your phone camera, or open this page on your phone.
          </p>
          {/* The QR encodes this same page (/get), so a scanning phone gets
              routed to its own store — never a hardcoded store link. */}
          <a href="https://www.groundops.app/get" aria-label="Open the GroundOps app download page">
            <img
              src="/get-app-qr.png"
              alt="QR code linking to the GroundOps Field app download"
              width={200}
              height={200}
              style={{ display: 'block', border: '1px solid #D5D7DA', borderRadius: '12px', padding: '12px' }}
            />
          </a>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center' }}>
            <a
              href={APP_STORE_URL}
              style={{
                display: 'inline-block',
                backgroundColor: '#181D27',
                color: '#FFFFFF',
                fontSize: '1rem',
                fontWeight: 600,
                textDecoration: 'none',
                padding: '0.875rem 2rem',
                borderRadius: '12px',
              }}
            >
              Download on the App Store
            </a>
            {PLAY_STORE_URL ? (
              <a
                href={PLAY_STORE_URL}
                style={{
                  display: 'inline-block',
                  backgroundColor: '#181D27',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  padding: '0.875rem 2rem',
                  borderRadius: '12px',
                }}
              >
                Get it on Google Play
              </a>
            ) : (
              // Both stores are always visible so the page reads as the full
              // platform story; Play stays a disabled tile until #603 lands
              // (set PLAY_STORE_URL in lib/app-links.ts — nothing else changes).
              <span
                aria-disabled="true"
                style={{
                  display: 'inline-block',
                  backgroundColor: '#F2F4F7',
                  color: '#717680',
                  fontSize: '1rem',
                  fontWeight: 600,
                  padding: '0.875rem 2rem',
                  borderRadius: '12px',
                  border: '1px dashed #D5D7DA',
                }}
              >
                Google Play — coming soon
              </span>
            )}
          </div>
        </>
      )}
    </main>
  )
}
